import { z } from "zod";

import {
	FindPersonContactMode,
	MAX_PEOPLE_PER_COMPANY,
	MIN_PEOPLE_PER_COMPANY,
} from "@signalist/shared-types";

import { selectAllScopeFields } from "@/pages/Private/pages/List/schema/select-all-scope.schema";

import {
	AssignStatus,
	InclusionFilterStatus,
	PriorityFilterStatus,
	SelectType,
} from "@/enum/list.enum";
import { api } from "@/redux/api";

/** Where a bulk find person takes its search profiles from (the API's `BulkFindPersonProfileSource`). */
export enum BulkFindPersonProfileSource {
	LIST = "LIST",
	PRESET = "PRESET",
	/** The bulk dialog's profiles, edited for this run only (PREC-4398). */
	INLINE = "INLINE",
}

/** The rows a bulk find person is about: the picked ids, or a select-all of the table's view. */
export const BulkFindPersonSelectionSchema = z.object({
	listId: z.number(),
	ids: z.array(z.number()).optional(),
	selectType: z.nativeEnum(SelectType).optional(),
	inclusionFilterStatus: z.array(z.nativeEnum(InclusionFilterStatus)).optional(),
	filter: z.record(z.string(), z.any()).optional(),
	priorityFilterStatus: z.array(z.nativeEnum(PriorityFilterStatus)).optional(),
	assignStatus: z.nativeEnum(AssignStatus).optional(),
	/** The table's search, tab statuses, reason sub-filters and signal filter (PREC-4443, PREC-4450). */
	...selectAllScopeFields,
});

export const BulkFindPersonSearchProfileSchema = z.object({
	source: z.nativeEnum(BulkFindPersonProfileSource),
	presetId: z.number().optional(),
	enrichmentFilterConfig: z.record(z.string(), z.unknown()).optional(),
});

export const BulkFindPersonRequestSchema = BulkFindPersonSelectionSchema.extend({
	searchProfile: BulkFindPersonSearchProfileSchema,
	peoplePerCompany: z.number().int().min(MIN_PEOPLE_PER_COMPANY).max(MAX_PEOPLE_PER_COMPANY),
	/** The run's "Use my contacts first" / "Find new people only" (PREC-3899); the list's when absent. */
	findPersonContactMode: z.nativeEnum(FindPersonContactMode).optional(),
	/** The plan the user confirmed: the search answers 409 when it no longer holds. */
	planKey: z.string(),
});

/** A selected row the search leaves out, with the reason in the list's words (the API's). */
export const BulkFindPersonSkippedRowSchema = z.object({
	listPersonId: z.number(),
	reason: z.string(),
});

export const BulkFindPersonResponseSchema = z.object({
	queued: z.coerce.number(),
	skipped: z.coerce.number(),
	skippedRows: z.array(BulkFindPersonSkippedRowSchema).optional(),
	ignoredRows: z.coerce.number().optional(),
});

/** A step the run charges for, as the API's quote names it. */
export const FIND_PEOPLE_QUOTE_STEPS = [
	"FIND_PERSON",
	"EMAIL",
	"PHONE",
	"COMPANY_CONTACT",
	"AI_COLUMNS",
] as const;

/** When a lookup runs for the people found, as the API decides it. */
export const FIND_PEOPLE_LOOKUP_TIMINGS = [
	"ON_ADD",
	"ON_ADD_WHEN_NO_EMAIL",
	"ON_ASSIGN",
	"ON_SEND",
	"OFF",
] as const;

const CreditRangeSchema = z.object({ min: z.coerce.number(), max: z.coerce.number() });

export const FindPeopleQuoteSchema = z.object({
	peoplePerCompany: z.number(),
	steps: z.array(
		z.object({
			kind: z.enum(FIND_PEOPLE_QUOTE_STEPS),
			maxUnits: z.coerce.number(),
			credits: CreditRangeSchema,
		})
	),
	total: CreditRangeSchema,
});

export const FindPeopleLookupSchema = z.object({
	kind: z.enum(["EMAIL", "PHONE", "COMPANY_CONTACT"]),
	timing: z.enum(FIND_PEOPLE_LOOKUP_TIMINGS),
	unitCredits: z.coerce.number(),
});

/**
 * What the search would do for a selection (PREC-4398): the companies and the rows it leaves out,
 * what the run can cost for each people per company (the API's quote, from the code that charges),
 * when each lookup runs, the balance against the search's pre-flight, and the plan's key.
 */
export const BulkFindPersonPlanSchema = z.object({
	companies: z.coerce.number(),
	skipped: z.coerce.number(),
	skippedRows: z.array(BulkFindPersonSkippedRowSchema),
	ignoredRows: z.coerce.number(),
	balance: z.coerce.number(),
	creditsEnforced: z.boolean(),
	preflightCredits: z.coerce.number(),
	balanceCoversSearch: z.boolean(),
	isTracker: z.boolean(),
	quotes: z.array(FindPeopleQuoteSchema),
	lookups: z.array(FindPeopleLookupSchema),
	planKey: z.string(),
});

export type BulkFindPersonSelection = z.infer<typeof BulkFindPersonSelectionSchema>;
export type BulkFindPersonRequest = z.infer<typeof BulkFindPersonRequestSchema>;
export type BulkFindPersonSkippedRow = z.infer<typeof BulkFindPersonSkippedRowSchema>;
export type BulkFindPersonResponse = z.infer<typeof BulkFindPersonResponseSchema>;
export type BulkFindPersonPlan = z.infer<typeof BulkFindPersonPlanSchema>;
export type FindPeopleQuote = z.infer<typeof FindPeopleQuoteSchema>;
export type FindPeopleLookup = z.infer<typeof FindPeopleLookupSchema>;

/** A plan asked for by one opening of the dialog, filed under that opening (apps/web §2). */
export interface PlanBulkFindPersonArg {
	sessionKey: string;
	selection: BulkFindPersonSelection;
}

/**
 * Bulk "Find person" endpoints, injected separately from list.api.ts (which is at its size cap).
 * The plan answers what the search would do before the user confirms, writing nothing; the search
 * returns {queued, skipped, skippedRows} at once, and the rows update over the websocket (the table
 * refetches on the bulk-find-person-updated events, so no tag is invalidated here).
 */
export const listFindPersonApi = api.injectEndpoints({
	endpoints: (builder) => ({
		planBulkFindPerson: builder.mutation<BulkFindPersonPlan, PlanBulkFindPersonArg>({
			query: ({ selection }) => ({
				url: "list/bulkFindPerson/plan",
				method: "POST",
				body: selection,
				responseSchema: BulkFindPersonPlanSchema,
			}),
		}),
		bulkFindPerson: builder.mutation<BulkFindPersonResponse, BulkFindPersonRequest>({
			query: (body) => ({
				url: "list/bulkFindPerson",
				method: "POST",
				body,
				responseSchema: BulkFindPersonResponseSchema,
			}),
		}),
	}),
});

export const { usePlanBulkFindPersonMutation, useBulkFindPersonMutation } = listFindPersonApi;
